import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, applyRoleFilter, sortComparator } from './helpers';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { matchesFilters } from './helpers/filterHelpers';
import {
  getUserPermissions,
  getUserRole,
} from '../../../helper/permissionsHelper';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import camelcaseKeys from 'camelcase-keys';

// Conversas de simulação (playground, inbox Channel::Studio) ENTRAM em
// allConversations pro playground funcionar (ContactPanel + thread leem como chat
// selecionado), mas NÃO podem vazar pras listas reais do atendente. Guard nos
// getters de lista é o ponto único — o ConversationFinder (server) não cobre a
// injeção client-side (getConversation + evento message.created).
const isSimulationChat = (conversation, rootGetters) =>
  rootGetters?.['inboxes/getInbox']?.(conversation.inbox_id)?.channel_type ===
  INBOX_TYPES.STUDIO;

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

const getters = {
  getAllConversations: ({ allConversations, chatSortFilter: sortKey }) => {
    return allConversations.sort((a, b) => sortComparator(a, b, sortKey));
  },

  getFilteredConversations: (
    { allConversations, chatSortFilter, appliedFilters },
    _,
    __,
    rootGetters
  ) => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);
    const currentUserTeamIds = (rootGetters['teams/getMyTeams'] || []).map(
      team => team.id
    );
    const teamsFetched = !!rootGetters['teams/getUIFlags']?.isFetched;

    return allConversations
      .filter(conversation => {
        const matchesFilterResult = matchesFilters(
          conversation,
          appliedFilters
        );
        const allowedForRole = applyRoleFilter(
          conversation,
          userRole,
          permissions,
          currentUserId,
          currentUserTeamIds,
          teamsFetched
        );

        return (
          matchesFilterResult &&
          allowedForRole &&
          !isSimulationChat(conversation, rootGetters)
        );
      })
      .sort((a, b) => sortComparator(a, b, chatSortFilter));
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: ({ selectedChatId, attachments }) => {
    return attachments[selectedChatId] || [];
  },
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const { message_type: messageType } = message;
      if (message.private) return false;

      return [MESSAGE_TYPE.OUTGOING, MESSAGE_TYPE.INCOMING].includes(
        messageType
      );
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;
    const filters = {
      ...activeFilters,
      mentionedConversationIds: _state.mentionedConversationIds,
      participatingConversationIds: _state.participatingConversationIds,
    };

    return _state.allConversations.filter(conversation => {
      const { assignee } = conversation.meta;
      const isAssignedToMe = assignee && assignee.id === currentUserID;
      const shouldFilter = applyPageFilters(conversation, filters);
      const isChatMine = isAssignedToMe && shouldFilter;

      return isChatMine && !isSimulationChat(conversation, rootGetters);
    });
  },
  getAppliedConversationFiltersV2: _state => {
    // TODO: Replace existing one with V2 after migrating the filters to use camelcase
    return _state.appliedFilters.map(camelcaseKeys);
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: (_state, _, __, rootGetters) => activeFilters => {
    const filters = {
      ...activeFilters,
      mentionedConversationIds: _state.mentionedConversationIds,
      participatingConversationIds: _state.participatingConversationIds,
    };
    return _state.allConversations.filter(conversation => {
      const isUnAssigned = !conversation.meta.assignee;
      const shouldFilter = applyPageFilters(conversation, filters);
      return (
        isUnAssigned &&
        shouldFilter &&
        !isSimulationChat(conversation, rootGetters)
      );
    });
  },
  getAllStatusChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);
    const currentUserTeamIds = (rootGetters['teams/getMyTeams'] || []).map(
      team => team.id
    );
    const teamsFetched = !!rootGetters['teams/getUIFlags']?.isFetched;

    // Na aba Menções o servidor é a autoridade: ele libera QUALQUER conversa em
    // que o atendente foi mencionado, inclusive de outro time ou já resolvida
    // (ConversationPolicy#show?). O cliente não recebe o dado de menção, então
    // reaplicar o filtro de escopo aqui derrubaria justamente as conversas pra
    // quais ele foi chamado.
    const isMentionsTab = activeFilters?.conversationType === 'mention';
    const filters = {
      ...activeFilters,
      mentionedConversationIds: _state.mentionedConversationIds,
      participatingConversationIds: _state.participatingConversationIds,
    };

    return _state.allConversations.filter(conversation => {
      const shouldFilter = applyPageFilters(conversation, filters);
      const allowedForRole =
        isMentionsTab ||
        applyRoleFilter(
          conversation,
          userRole,
          permissions,
          currentUserId,
          currentUserTeamIds,
          teamsFetched
        );

      return (
        shouldFilter &&
        allowedForRole &&
        !isSimulationChat(conversation, rootGetters)
      );
    });
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },

  getCopilotAssistant: _state => {
    return _state.copilotAssistant;
  },
};

export default getters;
